import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import './TestimonialsSection.css'
import { QUOTE_ICON_PATHS, STAR_PATH, TESTIMONIALS } from './testimonialsData'
import { FaRegCheckCircle } from 'react-icons/fa'

const AUTOPLAY_MS = 5000

function getVisible() {
  if (typeof window === 'undefined') return 3
  if (window.innerWidth >= 1024) return 3
  if (window.innerWidth >= 768) return 2
  return 1
}

function TestimonialCard({ item }) {
  return (
    <div className="tc-slide">
      <div className="tc-card relative">
        <svg className="tc-quote-icon" viewBox="0 0 24 24">
          {QUOTE_ICON_PATHS.map((d) => (
            <path key={d} d={d} />
          ))}
        </svg>
        <div className="tc-stars">
          {Array.from({ length: 5 }, (_, i) => (
            <svg className="tc-star" viewBox="0 0 24 24" key={i}>
              <path d={STAR_PATH} />
            </svg>
          ))}
        </div>
        <p className="tc-body normal-case">&quot;{item.quote}&quot;</p>
        <div className="tc-footer">
          <div className="tc-avatar">{item.initial}</div>
          <div className="tc-meta">
            <div className="tc-name">{item.name}</div>
            <div className="tc-loc">{item.loc}</div>
          </div>
          <FaRegCheckCircle fill='#F59E0B' size={18} />
        </div>
      </div>
    </div>
  )
}

export default function TestimonialsSection() {
  const sectionRef = useRef(null)
  const viewportRef = useRef(null)
  const trackRef = useRef(null)
  const timerRef = useRef(null)
  const pausedRef = useRef(false)
  const touchStartRef = useRef(0)

  const [visible, setVisible] = useState(getVisible)
  const [current, setCurrent] = useState(0)
  const [slideW, setSlideW] = useState(0)
  const [entered, setEntered] = useState(false)

  const total = TESTIMONIALS.length
  const maxIdx = Math.max(0, total - visible)

  useLayoutEffect(() => {
    const measure = () => {
      const el = trackRef.current?.children?.[0]
      if (el) setSlideW(el.getBoundingClientRect().width)
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [visible])

  useEffect(() => {
    const onResize = () => setVisible(getVisible())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    setCurrent((c) => Math.min(c, maxIdx))
  }, [maxIdx])

  useEffect(() => {
    const el = sectionRef.current
    if (!el) return undefined
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        setEntered(true)
        observer.disconnect()
      },
      { threshold: 0.12 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const stop = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const start = useCallback(() => {
    stop()
    if (pausedRef.current) return
    timerRef.current = window.setInterval(() => {
      setCurrent((c) => (c >= maxIdx ? 0 : c + 1))
    }, AUTOPLAY_MS)
  }, [stop, maxIdx])

  useEffect(() => {
    start()
    return stop
  }, [start, stop])

  const goTo = (idx) => setCurrent(Math.max(0, Math.min(maxIdx, idx)))

  const next = () => {
    goTo(current >= maxIdx ? 0 : current + 1)
    stop()
    start()
  }
  const prev = () => {
    goTo(current <= 0 ? maxIdx : current - 1)
    stop()
    start()
  }

  const handleMouseEnter = () => {
    pausedRef.current = true
    stop()
  }
  const handleMouseLeave = () => {
    pausedRef.current = false
    start()
  }

  const handleTouchStart = (e) => {
    touchStartRef.current = e.changedTouches[0].clientX
  }
  const handleTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touchStartRef.current
    if (Math.abs(dx) > 40) {
      if (dx < 0) next()
      else prev()
    }
  }

  return (
    <section
      id="testimonials-section"
      ref={sectionRef}
      className={`py-16 md:py-24 md:px-20 text-white${entered ? ' tc-visible' : ''}`}
    >
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, opacity: 0.04 }} xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="tc-dots-pattern" x="0" y="0" width="28" height="28" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="1" fill="#F59E0B" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#tc-dots-pattern)" />
        </svg>
        <div style={{ position: 'absolute', top: '-120px', left: '-80px', width: '500px', height: '500px', background: 'radial-gradient(circle,rgba(180,83,9,.18) 0%,transparent 70%)', borderRadius: '50%' }} />
        <div style={{ position: 'absolute', bottom: '-100px', right: '-60px', width: '420px', height: '420px', background: 'radial-gradient(circle,rgba(245,158,11,.12) 0%,transparent 70%)', borderRadius: '50%' }} />
        <svg width="260" height="260" style={{ position: 'absolute', top: '-40px', right: '-40px', opacity: 0.07 }} viewBox="0 0 260 260" fill="none">
          <circle cx="260" cy="0" r="100" stroke="#F59E0B" strokeWidth="1" />
          <circle cx="260" cy="0" r="150" stroke="#F59E0B" strokeWidth="0.6" />
          <circle cx="260" cy="0" r="200" stroke="#F59E0B" strokeWidth="0.4" strokeDasharray="4 6" />
        </svg>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg,transparent,rgba(245,158,11,.35),transparent)' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg,transparent,rgba(245,158,11,.35),transparent)' }} />
      </div>

      <div className="container relative z-10 mx-auto px-6 max-w-350">
        <div className="mb-12 md:mb-16 flex flex-col items-center text-center">
          <div className="tc-head-el flex items-center gap-3 mb-3">
            <span style={{ height: '1px', width: '32px', background: 'rgba(245,158,11,0.5)', display: 'block' }} />
            <span style={{ fontSize: '10px', letterSpacing: '0.4em', color: '#F59E0B', fontWeight: 500 }}>Seeker Experiences</span>
            <span style={{ height: '1px', width: '32px', background: 'rgba(245,158,11,0.5)', display: 'block' }} />
          </div>
          <h2 className="tc-head-el font-heading mt-2 text-3xl md:text-4xl leading-tight text-[#FBF3E4]" style={{ fontFamily: 'var(--font-heading),serif' }}>
            Authenticated{' '}
            <span style={{ background: 'linear-gradient(135deg,#F59E0B,#FCD34D,#D97706)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              Testimonials
            </span>
          </h2>
          <div className="tc-head-el mt-4 flex items-center gap-3">
            <span style={{ height: '1px', width: '48px', background: 'linear-gradient(90deg,transparent,rgba(245,158,11,0.4))', display: 'block' }} />
            <span style={{ fontSize: '20px', color: '#F59E0B', opacity: 0.7, lineHeight: 1 }}>ॐ</span>
            <span style={{ height: '1px', width: '48px', background: 'linear-gradient(90deg,rgba(245,158,11,0.4),transparent)', display: 'block' }} />
          </div>
          <p className="tc-head-el mt-4 max-w-xl text-xs md:text-sm normal-case" style={{ color: 'rgba(255,255,255,0.5)' }}>
            Real outcomes from seekers who have transformed their lives through Vedic wisdom.
          </p>
        </div>

        <div className="tc-viewport" ref={viewportRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
          <div ref={trackRef} className="tc-track py-1" style={{ transform: `translateX(${-(current * slideW)}px)` }}>
            {TESTIMONIALS.map((item) => (
              <TestimonialCard item={item} key={item.name} />
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', marginTop: '2.5rem' }}>
          <button type="button" className="tc-arrow" onClick={prev} disabled={maxIdx === 0} aria-label="Previous testimonials">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <div className="tc-dots">
            {Array.from({ length: maxIdx + 1 }, (_, i) => (
              <button
                key={i}
                type="button"
                className={`tc-dot${i === current ? ' tc-dot-active' : ''}`}
                style={{ width: i === current ? '22px' : '6px' }}
                onClick={() => {
                  goTo(i)
                  stop()
                  start()
                }}
                aria-label={`Go to testimonial page ${i + 1}`}
              />
            ))}
          </div>
          <button type="button" className="tc-arrow" onClick={next} disabled={maxIdx === 0} aria-label="Next testimonials">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  )
}
